import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Box, Spinner } from '@amzn/awsui-components-console';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from './ErrorBoundary';
import { ConfigProvider, useConfig } from '../contexts/ConfigContext';
import { ThemeProvider } from '../contexts/ThemeContext';
import { AuthenticationProvider } from '../contexts/AuthenticationContext';
import { AuthCallback } from './AuthCallback';
import { ConfigErrorPage } from './ConfigErrorPage';
import { GlobalLayout } from './GlobalLayout';
import { RequireAuth } from './RequireAuth';
import '@amzn/awsui-global-styles/polaris.css';

/**
 * QueryClient instance with default options for data fetching
 * - staleTime: 5 minutes — cached data is considered fresh for 5 min
 * - retry: 3 with exponential backoff (1s, 2s, 4s capped at 30s)
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  },
});

/**
 * Lazy-loaded components for code splitting
 *
 * Requirements:
 * - 7.3: Implement lazy loading for route-based code splitting
 */
const LandingPage = lazy(() =>
  import('./LandingPage').then((module) => ({ default: module.LandingPage })),
);

// Placeholder for Connections page - will be implemented in future tasks
const ConnectionsPage = lazy(() =>
  Promise.resolve({ default: () => <div>Connections Page - Coming Soon</div> }),
);

const ChannelsListPage = lazy(() =>
  import('./channels/ChannelsListPage').then((module) => ({ default: module.ChannelsListPage })),
);

const ChannelDetailPage = lazy(() =>
  import('./channels/ChannelDetailPage').then((module) => ({ default: module.ChannelDetailPage })),
);

const DocumentationLayout = lazy(() =>
  import('./documentation/DocumentationLayout').then((module) => ({
    default: module.DocumentationLayout,
  })),
);
const DocHomePage = lazy(() =>
  import('./documentation/DocHomePage').then((module) => ({ default: module.DocHomePage })),
);
const GettingStartedOverviewPage = lazy(() =>
  import('./documentation/GettingStartedOverviewPage').then((module) => ({
    default: module.GettingStartedOverviewPage,
  })),
);
const PrerequisitesPage = lazy(() =>
  import('./documentation/PrerequisitesPage').then((module) => ({
    default: module.PrerequisitesPage,
  })),
);
const InitialSetupPage = lazy(() =>
  import('./documentation/InitialSetupPage').then((module) => ({
    default: module.InitialSetupPage,
  })),
);
const VpnKeySecurityToolsPage = lazy(() =>
  import('./documentation/VpnKeySecurityToolsPage').then((module) => ({
    default: module.VpnKeySecurityToolsPage,
  })),
);
const VpnConfigurationPage = lazy(() =>
  import('./documentation/VpnConfigurationPage').then((module) => ({
    default: module.VpnConfigurationPage,
  })),
);
const TroubleshootingOverviewPage = lazy(() =>
  import('./documentation/TroubleshootingOverviewPage').then((module) => ({
    default: module.TroubleshootingOverviewPage,
  })),
);
const CommonVpnIssuesPage = lazy(() =>
  import('./documentation/CommonVpnIssuesPage').then((module) => ({
    default: module.CommonVpnIssuesPage,
  })),
);
const KeyEncryptionDecryptionPage = lazy(() =>
  import('./documentation/KeyEncryptionDecryptionPage').then((module) => ({
    default: module.KeyEncryptionDecryptionPage,
  })),
);
const DocumentHistoryPage = lazy(() =>
  import('./documentation/DocumentHistoryPage').then((module) => ({
    default: module.DocumentHistoryPage,
  })),
);

const NotFoundPage = lazy(() =>
  import('./NotFoundPage').then((module) => ({ default: module.NotFoundPage })),
);

/**
 * Loading Fallback Component
 *
 * Displays a centered spinner while lazy-loaded components are being fetched
 */
function LoadingFallback() {
  return (
    <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
      <Spinner size="large" />
      <Box variant="p" padding={{ top: 's' }}>
        Loading...
      </Box>
    </Box>
  );
}

/**
 * Main Application Component
 *
 * Wraps the application with:
 * - ErrorBoundary for error handling
 * - ConfigProvider for runtime configuration management
 * - AuthenticationProvider for authentication state management
 * - BrowserRouter for client-side routing
 * - Suspense for lazy loading with fallback UI
 *
 * Routes:
 * - / : Landing page (main application entry point)
 * - /callback : OAuth callback handler for Builder ID authentication
 * - /connections : Connections list page (authenticated users only)
 *
 * Requirements:
 * - 7.2: Use AWS UI Components (Cloudscape) for consistent UX
 * - 7.3: Implement lazy loading for route-based code splitting
 * - 7.4: Use React Context for authentication state management
 * - 7.5: Follow feature-based folder structure pattern from SCS Console
 * - Runtime configuration: Load configuration before rendering app
 */
export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ConfigProvider>
          <AppContent />
        </ConfigProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

/**
 * App Content Component
 *
 * Handles configuration loading states and renders the main application
 * once configuration is successfully loaded.
 *
 * States:
 * - Loading: Shows spinner while configuration loads
 * - Error: Shows error page with retry button
 * - Success: Renders main application with routing
 */
function AppContent() {
  const { config, loading, error, reload } = useConfig();

  // Show loading spinner while configuration loads
  if (loading) {
    return (
      <Box textAlign="center" padding={{ vertical: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>
          Loading configuration...
        </Box>
      </Box>
    );
  }

  // Show error page if configuration fails to load
  if (error || !config) {
    return <ConfigErrorPage onRetry={reload} />;
  }

  // Configuration loaded successfully - render main application
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthenticationProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/callback" element={<AuthCallback />} />
              <Route element={<GlobalLayout />}>
                <Route path="/" element={<LandingPage />} />
                <Route
                  path="/connections"
                  element={
                    <RequireAuth>
                      <ConnectionsPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/channels"
                  element={
                    <RequireAuth>
                      <ChannelsListPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/channels/:channelId"
                  element={
                    <RequireAuth>
                      <ChannelDetailPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/documentation"
                  element={
                    <RequireAuth>
                      <DocumentationLayout />
                    </RequireAuth>
                  }
                >
                  <Route index element={<DocHomePage />} />
                  <Route path="getting-started" element={<GettingStartedOverviewPage />} />
                  <Route path="getting-started/prerequisites" element={<PrerequisitesPage />} />
                  <Route path="getting-started/initial-setup" element={<InitialSetupPage />} />
                  <Route
                    path="getting-started/vpn-key-tools"
                    element={<VpnKeySecurityToolsPage />}
                  />
                  <Route
                    path="getting-started/vpn-configuration"
                    element={<VpnConfigurationPage />}
                  />
                  <Route path="troubleshooting" element={<TroubleshootingOverviewPage />} />
                  <Route
                    path="troubleshooting/common-vpn-issues"
                    element={<CommonVpnIssuesPage />}
                  />
                  <Route
                    path="troubleshooting/key-encryption-decryption"
                    element={<KeyEncryptionDecryptionPage />}
                  />
                  <Route path="history" element={<DocumentHistoryPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthenticationProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
